import { create } from 'zustand';
import { getLocalStorageData, saveLocalStorageData } from '@/utils/local-storage';
import { useShallow } from 'zustand/react/shallow';

interface NotificationStore {
  isUserOnActiveCasePage: boolean;
  isUserOnUnassignedCasePage: boolean;
  isUserOnSameTab: boolean;
  isBrowserNotificationEnabled: boolean;
  isHasNewNotification: boolean;
  isHasNewActiveCaseNotification: boolean;
  isHasNewUnassignedCaseNotification: boolean;
  enabledActiveCaseSound: boolean;
  enabledUnsignedCaseSound: boolean;
  enabledTicketingSound: boolean;

  // Actions
  actions: {
    setIsHasNewActiveCaseNotification: (isHasNewActiveCaseNotification: boolean) => void;
    setIsHasNewUnassignedCaseNotification: (isHasNewUnassignedCaseNotification: boolean) => void;
    setIsHasNewNotification: (isHasNewNotification: boolean) => void;
    setIsBrowserNotificationEnabled: (isEnabled: boolean) => void;
    setEnabledActiveCaseSound: (enabled: boolean) => void;
    setEnabledUnsignedCaseSound: (enabled: boolean) => void;
    setEnabledTicketingSound: (enabled: boolean) => void;
    setRouteContext: (pathname: string) => void;
    setIsUserOnSameTab: (isVisibleOrFocused: boolean) => void;
  };
}

// LocalStorage utilities
const ENABLED_ACTIVE_CASE_SOUND_KEY = 'enabled-active-case-sound';
const ENABLED_UNSIGNED_CASE_SOUND_KEY = 'enabled-unsigned-case-sound';
const ENABLED_TICKETING_SOUND_KEY = 'enabled-ticketing-sound';

const ACTIVE_CASE_PATH = '/active-cases';
const UNASSIGNED_CASE_PATH = '/unassigned-cases';

const checkIsUserOnActiveCasePage = () => {
  const pathname = window.location.pathname;
  return pathname.includes(ACTIVE_CASE_PATH) ? true : false;
};

const checkIsUserOnUnassignedCasePage = () => {
  const pathname = window.location.pathname;
  return pathname.includes(UNASSIGNED_CASE_PATH) ? true : false;
};

export const notificationStore = create<NotificationStore>((set) => ({
  isUserOnActiveCasePage: checkIsUserOnActiveCasePage() ? true : false,
  isUserOnUnassignedCasePage: checkIsUserOnUnassignedCasePage() ? true : false,
  isUserOnSameTab: document.visibilityState === 'visible' || document.hasFocus(),
  isBrowserNotificationEnabled: Notification.permission === 'granted' ? true : false,
  enabledActiveCaseSound: getLocalStorageData(ENABLED_ACTIVE_CASE_SOUND_KEY) === 'true' ? true : false,
  enabledUnsignedCaseSound: getLocalStorageData(ENABLED_UNSIGNED_CASE_SOUND_KEY) === 'true' ? true : false,
  enabledTicketingSound: getLocalStorageData(ENABLED_TICKETING_SOUND_KEY) === 'true' ? true : false,
  isHasNewNotification: false,
  isHasNewActiveCaseNotification: false,
  isHasNewUnassignedCaseNotification: false,

  actions: {
    setIsHasNewActiveCaseNotification: (isHasNewActiveCaseNotification: boolean) => {
      set({ isHasNewActiveCaseNotification: isHasNewActiveCaseNotification });
    },
    setIsHasNewUnassignedCaseNotification: (isHasNewUnassignedCaseNotification: boolean) => {
      set({ isHasNewUnassignedCaseNotification: isHasNewUnassignedCaseNotification });
    },
    setIsHasNewNotification: (isHasNewNotification: boolean) => {
      set({ isHasNewNotification: isHasNewNotification });
    },

    setIsBrowserNotificationEnabled: (isEnabled: boolean) => {
      set({ isBrowserNotificationEnabled: isEnabled });
    },

    setEnabledActiveCaseSound: (enabled: boolean) => {
      saveLocalStorageData(ENABLED_ACTIVE_CASE_SOUND_KEY, enabled.toString());
      set({ enabledActiveCaseSound: enabled });
    },

    setEnabledUnsignedCaseSound: (enabled: boolean) => {
      saveLocalStorageData(ENABLED_UNSIGNED_CASE_SOUND_KEY, enabled.toString());
      set({ enabledUnsignedCaseSound: enabled });
    },

    setEnabledTicketingSound: (enabled: boolean) => {
      saveLocalStorageData(ENABLED_TICKETING_SOUND_KEY, enabled.toString());
      set({ enabledTicketingSound: enabled });
    },

    setRouteContext: (pathname: string) => {
      const isOnActive = pathname.includes(ACTIVE_CASE_PATH);
      const isOnUnassigned = pathname.includes(UNASSIGNED_CASE_PATH);
      set({
        isUserOnActiveCasePage: isOnActive,
        isUserOnUnassignedCasePage: isOnUnassigned,
      });
    },

    setIsUserOnSameTab: (isVisibleOrFocused: boolean) => {
      set({ isUserOnSameTab: isVisibleOrFocused });
    },
  },
}));

// selectors
export const useIsBrowserNotificationEnabled = () =>
  notificationStore(useShallow((state) => state.isBrowserNotificationEnabled));
export const useEnabledActiveCaseSound = () => notificationStore(useShallow((state) => state.enabledActiveCaseSound));
export const useEnabledUnsignedCaseSound = () =>
  notificationStore(useShallow((state) => state.enabledUnsignedCaseSound));
export const useEnabledTicketingSound = () => notificationStore(useShallow((state) => state.enabledTicketingSound));
export const useIsUserOnSameTab = () => notificationStore(useShallow((state) => state.isUserOnSameTab));
export const useIsUserOnActiveCasePage = () => notificationStore(useShallow((state) => state.isUserOnActiveCasePage));
export const useIsUserOnUnassignedCasePage = () =>
  notificationStore(useShallow((state) => state.isUserOnUnassignedCasePage));
export const useIsHasNewNotification = () => notificationStore(useShallow((state) => state.isHasNewNotification));
export const useIsHasNewActiveCaseNotification = () =>
  notificationStore(useShallow((state) => state.isHasNewActiveCaseNotification));
export const useIsHasNewUnassignedCaseNotification = () =>
  notificationStore(useShallow((state) => state.isHasNewUnassignedCaseNotification));
// actions
export const useNotificationActions = () => notificationStore(useShallow((state) => state.actions));
